import React, { useState, useEffect } from 'react';
import type { OnlineUsersComponent } from '../../../types/builder';
import { Users } from 'lucide-react';

interface OnlineUsersRendererProps {
  component: OnlineUsersComponent;
}

export const OnlineUsersRenderer: React.FC<OnlineUsersRendererProps> = ({ component }) => {
  const { settings } = component;
  const [displayCount, setDisplayCount] = useState(settings.count);

  useEffect(() => {
    if (!settings.animateCount) {
      setDisplayCount(settings.count);
      return;
    }

    const interval = setInterval(() => {
      const fluctuation = settings.countFluctuation || 50;
      const randomChange = Math.floor(Math.random() * fluctuation * 2) - fluctuation;
      const newCount = Math.max(1, settings.count + randomChange);
      setDisplayCount(newCount);
    }, 2000);

    return () => clearInterval(interval);
  }, [settings.animateCount, settings.count, settings.countFluctuation]);

  const alignmentClass = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
  }[settings.alignment];

  const style = settings.style || 'default';

  // Default Style
  if (style === 'default') {
    return (
      <div
        style={{
          backgroundColor: settings.backgroundColor,
          padding: `${settings.padding}px`,
        }}
        className={`flex ${alignmentClass}`}
      >
        <div className="flex items-center gap-3 px-6 py-4 rounded-xl border border-gray-700/50 backdrop-blur-sm">
          <div className="relative">
            {settings.icon ? (
              <img src={settings.icon} alt="icon" className="w-8 h-8 object-contain" />
            ) : (
              <Users size={32} style={{ color: settings.iconColor }} />
            )}
            {settings.showPulse && (
              <span className="absolute -top-1 -right-1 flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
              </span>
            )}
          </div>
          <div>
            <div 
              className="text-2xl font-bold transition-all duration-300" 
              style={{ color: settings.textColor }}
            >
              {displayCount.toLocaleString()}
            </div>
            <div className="text-sm opacity-75" style={{ color: settings.textColor }}>
              {settings.title}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Compact Style
  if (style === 'compact') {
    return (
      <div
        style={{
          backgroundColor: settings.backgroundColor,
          padding: `${settings.padding}px`,
        }}
        className={`flex ${alignmentClass}`}
      >
        <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-black/20">
          <div className="relative">
            {settings.icon ? (
              <img src={settings.icon} alt="icon" className="w-5 h-5 object-contain" />
            ) : (
              <Users size={20} style={{ color: settings.iconColor }} />
            )}
            {settings.showPulse && (
              <span className="absolute -top-0.5 -right-0.5 flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
              </span>
            )}
          </div>
          <div className="flex items-baseline gap-1">
            <span 
              className="text-lg font-bold transition-all duration-300" 
              style={{ color: settings.textColor }}
            >
              {displayCount.toLocaleString()}
            </span>
            <span className="text-xs opacity-75" style={{ color: settings.textColor }}>
              {settings.title}
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Minimal Style
  if (style === 'minimal') {
    return (
      <div
        style={{
          backgroundColor: settings.backgroundColor,
          padding: `${settings.padding}px`,
        }}
        className={`flex ${alignmentClass}`}
      >
        <div className="flex items-center gap-2">
          <div className="relative">
            {settings.icon ? (
              <img src={settings.icon} alt="icon" className="w-6 h-6 object-contain" />
            ) : (
              <Users size={24} style={{ color: settings.iconColor }} />
            )}
            {settings.showPulse && (
              <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
              </span>
            )}
          </div>
          <div className="flex items-baseline gap-1.5">
            <span 
              className="text-xl font-semibold transition-all duration-300" 
              style={{ color: settings.textColor }}
            >
              {displayCount.toLocaleString()}
            </span>
            <span className="text-sm" style={{ color: settings.textColor, opacity: 0.7 }}>
              {settings.title}
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Modern Style
  if (style === 'modern') {
    return (
      <div
        style={{
          backgroundColor: settings.backgroundColor,
          padding: `${settings.padding}px`,
        }}
        className={`flex ${alignmentClass}`}
      >
        <div className="flex items-center gap-4 px-8 py-5 rounded-full bg-gradient-to-r from-purple-600/20 to-blue-600/20 border border-purple-500/30 shadow-lg shadow-purple-500/20 backdrop-blur-md">
          <div className="relative">
            {settings.icon ? (
              <img src={settings.icon} alt="icon" className="w-10 h-10 object-contain drop-shadow-lg" />
            ) : (
              <Users size={40} style={{ color: settings.iconColor }} className="drop-shadow-lg" />
            )}
            {settings.showPulse && (
              <span className="absolute -top-1 -right-1 flex h-4 w-4">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-4 w-4 bg-green-500 ring-2 ring-white"></span>
              </span>
            )}
          </div>
          <div>
            <div 
              className="text-3xl font-extrabold transition-all duration-300 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent drop-shadow" 
              style={{ WebkitTextFillColor: 'transparent', backgroundImage: `linear-gradient(to right, ${settings.textColor}, ${settings.textColor}dd)` }}
            >
              {displayCount.toLocaleString()}
            </div>
            <div className="text-sm font-medium tracking-wide" style={{ color: settings.textColor, opacity: 0.8 }}>
              {settings.title}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Fallback to default
  return null;
};

